/**
 * **룬 최적화 셈** — SWEX 계정 JSON 의 룬 전부를 놓고 "세트별 최고속도" 와 "최고 전투력" 을 찾는다.
 *
 * 파일은 브라우저에서만 읽는다(보유몹 탭과 같은 원칙 — 계정 JSON 에는 룬·아이템·우편까지
 * 들어 있어 서버에 올릴 이유가 없다). 여기 있는 것은 순수 셈뿐이고 화면은 `RunePage` 가 그린다.
 *
 * 룬 값의 생김새(실측):
 *   `{ set_id, slot_no, class, upgrade_curr, occupied_type, occupied_id,
 *      pri_eff: [옵종류, 값], prefix_eff: [옵종류, 값], sec_eff: [[옵종류, 값, 보석여부, 연마값], …] }`
 *   class 가 11 이상이면 고대 룬(class − 10 이 성급).
 *   부옵의 실제 값은 **값 + 연마값** 이다.
 */

/** 룬 옵션 종류 — 게임 상수 */
export const EFF = {
    HP_FLAT: 1, HP_PCT: 2, ATK_FLAT: 3, ATK_PCT: 4, DEF_FLAT: 5, DEF_PCT: 6,
    SPD: 8, CRIT_RATE: 9, CRIT_DMG: 10, RESIST: 11, ACCURACY: 12
} as const;

export const EFF_LABEL: Record<number, string> = {
    1: '체력', 2: '체력', 3: '공격력', 4: '공격력', 5: '방어력', 6: '방어력',
    8: '공격속도', 9: '치명확률', 10: '치명피해', 11: '효과저항', 12: '효과적중'
};

/** 게임 룬 창의 표기 — 띄어쓰기가 다르다 */
export const EFF_LABEL_GAME: Record<number, string> = {
    1: '체력', 2: '체력', 3: '공격력', 4: '공격력', 5: '방어력', 6: '방어력',
    8: '공격속도', 9: '치명 확률', 10: '치명 피해', 11: '효과 저항', 12: '효과 적중'
};

/** 세트 효과 — 게임 룬 창의 "N세트 : …" 줄 */
export const SET_EFFECT: Record<number, string> = {
    1: '2세트 : 체력 +15%', 2: '2세트 : 방어력 +15%', 3: '4세트 : 공격속도 +25%', 4: '2세트 : 치명 확률 +12%',
    5: '4세트 : 치명 피해 +40%', 6: '2세트 : 효과 적중 +20%', 7: '2세트 : 효과 저항 +20%', 8: '4세트 : 공격력 +35%',
    10: '4세트 : 기절 확률 +25%', 11: '4세트 : 흡혈 35%', 13: '4세트 : 추가 턴 확률 22%', 14: '2세트 : 피격 시 공격 게이지 +4%',
    15: '2세트 : 첫 턴 면역', 16: '2세트 : 아군 보호막 15%', 17: '2세트 : 반격 확률 15%', 18: '2세트 : 최대 체력 파괴 4%',
    19: '2세트 : 아군 공격력 +8%', 20: '2세트 : 아군 방어력 +8%', 21: '2세트 : 아군 체력 +8%', 22: '2세트 : 아군 효과 적중 +10%',
    23: '2세트 : 아군 효과 저항 +10%', 24: '2세트 : 봉인 확률 +18%', 25: '2세트 : 무형'
};

/**
 * 연마석으로 올릴 수 있는 최대치 — 전설 연마석 기준. [보통 룬, 고대 룬].
 * 치확·치피·효적·효저는 연마가 없다. 값은 swsiege 의 개선 후보(공격력 30/34, 공% 10/12, 공속 5/6)와
 * 게임 표를 맞춘 것이다.
 */
export const GRIND_MAX: Record<number, [number, number]> = {
    1: [550, 610], 2: [10, 12], 3: [30, 34], 4: [10, 12], 5: [30, 34], 6: [10, 12], 8: [5, 6]
};

export const QUALITY: Record<number, { name: string; color: string }> = {
    1: { name: '일반', color: '#e8e8e8' }, 2: { name: '마법', color: '#7ed957' }, 3: { name: '희귀', color: '#5fb0ff' },
    4: { name: '영웅', color: '#c77dff' }, 5: { name: '전설', color: '#ff9b3d' }
};

/** 옵션을 "공격력 +63%" 처럼 적는다 */
export function effText(type: number, value: number): string {
    const pct = type === 2 || type === 4 || type === 6 || type >= 9;
    return `${EFF_LABEL[type] || `옵션${type}`} +${value}${pct ? '%' : ''}`;
}

/** 세트 번호 → 이름 (게임 안 표기) */
export const SET_NAME: Record<number, string> = {
    1: '활력', 2: '수호', 3: '신속', 4: '칼날', 5: '격노', 6: '집중', 7: '인내', 8: '맹공',
    10: '절망', 11: '흡혈', 13: '폭주', 14: '응보', 15: '의지', 16: '보호', 17: '반격',
    18: '파괴', 19: '투지', 20: '결의', 21: '강화', 22: '명중', 23: '저항', 24: '봉인', 25: '무형',
    99: '태고'
};
export const setName = (id: number) => SET_NAME[id] || `세트${id}`;

/** 세트 한 벌의 크기와 스탯 효과(있는 것만) */
const SET_PIECES: Record<number, number> = { 3: 4, 5: 4, 8: 4, 10: 4, 11: 4, 13: 4 };
const piecesOf = (id: number) => SET_PIECES[id] || 2;

/** 무형 — 다른 세트의 모자란 한 조각이 되어 준다 */
const INTANGIBLE = 25;

/**
 * **세트별 룬 개수 — 무형을 채워 넣은 뒤.** 무형 룬은 그 자체로 세트가 아니라, 다른 세트를 완성하는
 * 조각으로 센다(신속 3 + 무형 1 = 신속 4벌). 무형이 여러 세트를 채울 수 있으면 4벌 세트를 먼저,
 * 그다음 적게 모자란 세트를 채운다. 채울 곳이 없으면 아무 세트도 되지 않는다.
 */
export function setCounts(runes: Array<{ set: number }>): Record<number, number> {
    const count: Record<number, number> = {};
    let wild = 0;
    for (const r of runes) {
        if (r.set === INTANGIBLE) wild += 1;
        else count[r.set] = (count[r.set] || 0) + 1;
    }
    while (wild > 0) {
        let pick: { id: number; need: number; four: boolean } | null = null;
        for (const [k, n] of Object.entries(count)) {
            const id = Number(k);
            const rem = n % piecesOf(id);
            if (rem === 0) continue;
            const need = piecesOf(id) - rem;
            if (need > wild) continue;
            const four = piecesOf(id) === 4;
            if (!pick || (four && !pick.four) || (four === pick.four && need < pick.need)) pick = { id, need, four };
        }
        if (!pick) break;
        count[pick.id] += pick.need;
        wild -= pick.need;
    }
    return count;
}

export interface Rune {
    id: number;
    set: number;
    slot: number;
    ancient: boolean;
    stars: number;
    up: number;
    /** 등급 1 일반 · 2 마법 · 3 희귀 · 4 영웅 · 5 전설 (고대는 rank 에서 10 을 뺀 값) */
    quality: number;
    /** 판매가 (마나) */
    sell: number;
    main: [number, number];
    prefix: [number, number] | null;
    /** [종류, 값(연마 포함), 연마값, 보석으로 바꿨나(1)] */
    subs: Array<[number, number, number, number]>;
    /** 종류별 합 (주옵·접두·부옵·연마 전부) */
    total: Record<number, number>;
    /** 끼고 있는 몹의 unit_master_id (창고에 있으면 null) */
    unitMasterId: number | null;
}

/** 전투력에 들어가는 계정 설정 — 건물 보너스 */
export interface AccountBonus {
    /** 공격력 % 를 올리는 건물(고대의 검, deco 38)의 레벨 — 1레벨에 1% */
    atkBuilding: number;
}

/** 계정 JSON 에서 룬 전부(착용 + 창고)를 편다 */
export function runesFromAccount(account: any): { runes: Rune[]; equipped: number; stored: number; wizardName: string | null; bonus: AccountBonus } {
    if (!account || !Array.isArray(account.unit_list)) throw new Error('SWEX 계정 JSON 이 아닙니다 (unit_list 가 없습니다)');

    const out: Rune[] = [];
    const push = (raw: any, unitMasterId: number | null) => {
        if (!raw || !Number.isFinite(Number(raw.rune_id))) return;
        const cls = Number(raw.class) || 0;
        const main: [number, number] = [Number(raw.pri_eff?.[0]) || 0, Number(raw.pri_eff?.[1]) || 0];
        const pf = raw.prefix_eff;
        const prefix: [number, number] | null = Array.isArray(pf) && Number(pf[0]) > 0 ? [Number(pf[0]), Number(pf[1]) || 0] : null;
        const subs: Array<[number, number, number, number]> = [];
        for (const s of raw.sec_eff || []) {
            if (!Array.isArray(s) || !Number(s[0])) continue;
            const grind = Number(s[3]) || 0;
            subs.push([Number(s[0]), (Number(s[1]) || 0) + grind, grind, Number(s[2]) ? 1 : 0]);
        }
        const rank = Number(raw.rank) || 0;
        const total: Record<number, number> = {};
        const add = (t: number, v: number) => { if (t) total[t] = (total[t] || 0) + v; };
        add(main[0], main[1]);
        if (prefix) add(prefix[0], prefix[1]);
        for (const [t, v] of subs) add(t, v);

        out.push({
            id: Number(raw.rune_id),
            set: Number(raw.set_id) || 0,
            slot: Number(raw.slot_no) || 0,
            ancient: cls >= 11,
            stars: cls >= 11 ? cls - 10 : cls,
            up: Number(raw.upgrade_curr) || 0,
            quality: rank > 10 ? rank - 10 : rank,
            sell: Number(raw.sell_value) || 0,
            main, prefix, subs, total, unitMasterId
        });
    };

    let equipped = 0;
    for (const u of account.unit_list) {
        const rs = u?.runes;
        const list = Array.isArray(rs) ? rs : (rs && typeof rs === 'object' ? Object.values(rs) : []);
        for (const r of list) { push(r, Number(u.unit_master_id) || null); equipped += 1; }
    }
    const loose = Array.isArray(account.runes) ? account.runes : [];
    for (const r of loose) push(r, null);

    const decos: any[] = Array.isArray(account.deco_list) ? account.deco_list : [];
    const atkBuilding = Number(decos.find((d) => Number(d?.master_id) === ATK_BUILDING_ID)?.level) || 0;

    return { runes: out, equipped, stored: loose.length, wizardName: account.wizard_info?.wizard_name || null, bonus: { atkBuilding } };
}

/**
 * 공격력 % 건물의 deco master_id.
 *
 * 두 계정(애플톡·점심)의 전투력 차이 99 = 9 × 11 이 정확히 이 건물의 레벨 차(20 vs 9)와
 * 맞았다 — 다른 건물은 레벨 차가 11인 것이 없다. 게임 안 이름표는 못 구했고(SWARFARM 도
 * 건물 표를 공개하지 않는다) 1레벨에 1% 인 건물은 "고대의 검" 뿐이라 그것으로 본다.
 */
export const ATK_BUILDING_ID = 38;

// ── 고점 기준: 풀강화 · 변환 · 풀연마 ───────────────────────────────────────

/** +15 주옵션 값 — [5성, 6성]. 고대 룬도 같다(고대는 부옵 굴림만 다르다) */
const MAIN_MAX: Record<number, [number, number]> = {
    1: [2088, 2448], 2: [51, 63], 3: [135, 160], 4: [51, 63], 5: [135, 160], 6: [51, 63],
    8: [39, 42], 9: [47, 58], 10: [65, 80], 11: [51, 64], 12: [51, 64]
};

/** 전설 보석으로 바꿨을 때의 값 — [보통, 고대]. swsiege 의 개선 후보 값과 맞췄다(공 40/44, 치확 9, 치피 10, 공% 13) */
export const GEM_MAX: Record<number, [number, number]> = {
    1: [580, 640], 2: [13, 15], 3: [40, 44], 4: [13, 15], 5: [40, 44], 6: [13, 15],
    8: [10, 11], 9: [9, 10], 10: [10, 11], 11: [11, 12], 12: [11, 12]
};

/**
 * **자리마다 붙지 않는 옵션** — 게임 규칙이다. 1번 룬(주옵션 평방 자리가 아닌 평공 자리)에는
 * 방어력·방어력% 가, 3번 룬에는 공격력·공격력% 가 부옵션으로도 **보석으로도** 안 붙는다.
 * 나머지 자리는 제한이 없다(주옵션과 같은 옵션이 부옵으로 겹치지 않는 것은 따로 걸러낸다).
 *
 * 실제 계정의 1·3번 룬 1,554 개를 훑어 어기는 것이 하나도 없음을 확인하고 넣었다.
 */
export const SLOT_BAN: Record<number, number[]> = {
    1: [EFF.DEF_FLAT, EFF.DEF_PCT],
    3: [EFF.ATK_FLAT, EFF.ATK_PCT]
};

export type Objective = 'power' | 'speed';

/**
 * 옵션 1점이 목표에 얼마나 값어치 있나 — 어느 부옵을 버리고 무엇으로 바꿀지 고를 때만 쓴다.
 * 전투력: 공% 9 · 치피 10 · 치확 3 · 평공 1 (전투력 식의 기울기). 최고속: 공속만.
 */
const WORTH: Record<Objective, Record<number, number>> = {
    power: { [EFF.ATK_PCT]: 9, [EFF.CRIT_DMG]: 10, [EFF.CRIT_RATE]: 3, [EFF.ATK_FLAT]: 1 },
    speed: { [EFF.SPD]: 1 }
};

/**
 * **고점 기준의 룬** — 이 룬을 끝까지 키우면 어디까지 가나.
 *
 * - 풀강화: 주옵션을 +15 값으로(강화 중 부옵이 어디에 붙을지는 알 수 없어 부옵은 안 올린다)
 * - 변환: 목표에 가장 쓸모없는 부옵 하나를 전설 보석으로 바꾼다 — 목표에 가장 값진 옵션 중
 *   이 룬에 아직 없는 것으로(주옵·접두·다른 부옵과 겹칠 수 없다). 이미 보석을 쓴 룬은 그 자리를 다시 고른다
 * - 풀연마: 연마되는 부옵은 전설 연마석 최대치로(바꾼 옵션도)
 * 룬 번호·세트·자리는 그대로라 화면과 셈이 그대로 쓴다.
 */
export function peakRune(r: Rune, objective: Objective): Rune {
    const anc = r.ancient ? 1 : 0;
    const starIdx = r.stars >= 6 ? 1 : 0;
    const worth = WORTH[objective];
    const w = (type: number) => worth[type] || 0;

    // 풀강화 — 주옵션
    const mainMax = MAIN_MAX[r.main[0]];
    const main: [number, number] = mainMax && r.up < 15 ? [r.main[0], mainMax[starIdx]] : [r.main[0], r.main[1]];

    // 변환 — 버릴 부옵과 바꿀 옵션
    let subs = r.subs.map(([t, v, g, gem]) => [t, v, g, gem] as [number, number, number, number]);
    if (subs.length) {
        // 이미 있는 옵션 + 그 자리에 아예 안 붙는 옵션은 바꿀 후보에서 뺀다
        const taken = new Set<number>([
            r.main[0], ...(r.prefix ? [r.prefix[0]] : []), ...subs.map((x) => x[0]),
            ...(SLOT_BAN[r.slot] || [])
        ]);
        // 바꿀 후보: 목표 값어치 × 보석값 이 가장 큰, 이 룬에 없는 옵션
        let target: number | null = null;
        let targetGain = 0;
        for (const tStr of Object.keys(GEM_MAX)) {
            const t = Number(tStr);
            if (taken.has(t) || !w(t)) continue;
            const gain = w(t) * (GEM_MAX[t][anc] + (GRIND_MAX[t] ? GRIND_MAX[t][anc] : 0));
            if (gain > targetGain) { targetGain = gain; target = t; }
        }
        if (target !== null) {
            // 버릴 부옵: 목표 값어치가 가장 작은 것(보석을 이미 쓴 자리가 있으면 그 자리 — 보석은 룬에 하나)
            const gemAt = subs.findIndex((x) => x[3] > 0);
            let drop = gemAt >= 0 ? gemAt : 0;
            if (gemAt < 0) {
                let worst = Infinity;
                subs.forEach(([t, v], i) => {
                    const val = w(t) * v;
                    if (val < worst) { worst = val; drop = i; }
                });
            }
            const dropped = subs[drop];
            // 버리는 것이 바꿀 것보다 값지면 바꾸지 않는다
            const keepVal = w(dropped[0]) * (dropped[1] - dropped[2] + (GRIND_MAX[dropped[0]] ? GRIND_MAX[dropped[0]][anc] : 0));
            if (targetGain > keepVal) subs[drop] = [target, GEM_MAX[target][anc], 0, 1];
        }
    }

    // 이미 보석을 쓴 부옵은 전설 보석 값까지 올린다(낮은 보석을 썼을 수 있다) · 풀연마
    subs = subs.map(([t, v, g, gem]) => {
        let base = v - g;
        if (gem && GEM_MAX[t]) base = Math.max(base, GEM_MAX[t][anc]);
        const max = GRIND_MAX[t] ? GRIND_MAX[t][anc] : 0;
        return max ? [t, base + max, max, gem] : [t, base, 0, gem];
    });

    const total: Record<number, number> = {};
    const add = (t: number, v: number) => { if (t) total[t] = (total[t] || 0) + v; };
    add(main[0], main[1]);
    if (r.prefix) add(r.prefix[0], r.prefix[1]);
    for (const [t, v] of subs) add(t, v);

    return { ...r, up: 15, main, subs, total };
}

// ── 유물(아티팩트) ──────────────────────────────────────────────────────────

/** 몹이 낀 유물 하나 — 속성 유물(type 1)과 유형 유물(type 2) */
export interface Artifact {
    rid: number;
    /** 1 속성 · 2 유형 */
    type: number;
    /** 속성 유물이면 1 물 · 2 불 · 3 바람 · 4 빛 · 5 어둠 */
    attribute: number;
    /** 유형 유물이면 1 공격형 · 2 방어형 · 3 체력형 · 4 서포트형 */
    unitStyle: number;
    level: number;
    /** 1 일반 … 5 전설 */
    rank: number;
    /** [100 체력 | 101 공격력 | 102 방어력, 값] */
    main: [number, number];
    /** [효과 번호, 값] 넷 */
    subs: Array<[number, number]>;
}

export const ARTIFACT_MAIN: Record<number, string> = { 100: '체력', 101: '공격력', 102: '방어력' };
export const UNIT_STYLE_NAME: Record<number, string> = { 1: '공격형', 2: '방어형', 3: '체력형', 4: '서포트형', 5: '재료' };

/** 유물 부옵션 문구 — 게임 아티팩트 창 표기 그대로 (사용자 2026-10-21 게임 화면: "[2번 스킬] 효과적중 6%증가" · "체력의 1.1%만큼 추가피해") */
export function artifactEffectText(id: number, v: number): string {
    const attr: Record<number, string> = { 0: '불', 1: '물', 2: '바람', 3: '빛', 4: '어둠' };
    const table: Record<number, string> = {
        200: `잃은 체력 비례 공격력 최대 ${v}%증가`, 201: `잃은 체력 비례 방어력 최대 ${v}%증가`, 202: `잃은 체력 비례 공격속도 최대 ${v}%증가`,
        203: `행동불능 시 공격속도 ${v}%증가`, 204: `공격 강화효과 ${v}%증가`, 205: `방어 강화효과 ${v}%증가`, 206: `공격속도 증가효과 ${v}%증가`,
        207: `치명확률 증가효과 ${v}%증가`, 208: `반격 피해 ${v}%증가`, 209: `협력공격 피해 ${v}%증가`, 210: `폭탄 피해 ${v}%증가`,
        211: `반사 피해 ${v}%증가`, 212: `분쇄타 피해 ${v}%증가`, 213: `행동불능 시 받는 피해 ${v}%감소`, 214: `받는 치명피해 ${v}%감소`,
        215: `흡혈 ${v}%증가`, 216: `부활 시 체력 ${v}%증가`, 217: `부활 시 공격게이지 ${v}%증가`,
        218: `체력의 ${v}%만큼 추가피해`, 219: `공격력의 ${v}%만큼 추가피해`, 220: `방어력의 ${v}%만큼 추가피해`, 221: `공격속도의 ${v}%만큼 추가피해`,
        222: `적 체력이 많을수록 치명피해 최대 ${v}%증가`, 223: `적 체력이 적을수록 치명피해 최대 ${v}%증가`,
        224: `내 턴 단일대상 스킬 치명피해 ${v}%증가`, 225: `반격/협력공격 피해 ${v}%증가`, 226: `공격/방어 강화효과 ${v}%증가`,
        400: `[1번 스킬] 치명피해 ${v}%증가`, 401: `[2번 스킬] 치명피해 ${v}%증가`, 402: `[3번 스킬] 치명피해 ${v}%증가`, 403: `[4번 스킬] 치명피해 ${v}%증가`,
        404: `[1번 스킬] 회복량 ${v}%증가`, 405: `[2번 스킬] 회복량 ${v}%증가`, 406: `[3번 스킬] 회복량 ${v}%증가`,
        407: `[1번 스킬] 효과적중 ${v}%증가`, 408: `[2번 스킬] 효과적중 ${v}%증가`, 409: `[3번 스킬] 효과적중 ${v}%증가`,
        410: `[3/4번 스킬] 치명피해 ${v}%증가`, 411: `첫 공격 치명피해 ${v}%증가`
    };
    if (table[id]) return table[id];
    if (id >= 300 && id <= 304) return `${attr[id - 300]} 속성에게 주는 피해 ${v}%증가`;
    if (id >= 305 && id <= 309) return `${attr[id - 305]} 속성에게 받는 피해 ${v}%감소`;
    return `효과 ${id} ${v}`;
}

/** 원본 유물 → Artifact. 값이 이상하면 null */
export function artifactOf(raw: any): Artifact | null {
    if (!raw || !Number(raw.rid)) return null;
    const pri = Array.isArray(raw.pri_effect) ? raw.pri_effect : [];
    return {
        rid: Number(raw.rid),
        type: Number(raw.type) || 0,
        attribute: Number(raw.attribute) || 0,
        unitStyle: Number(raw.unit_style) || 0,
        level: Number(raw.level) || 0,
        rank: Number(raw.rank) || 0,
        main: [Number(pri[0]) || 0, Number(pri[1]) || 0],
        subs: (Array.isArray(raw.sec_effects) ? raw.sec_effects : [])
            .filter((e: any) => Array.isArray(e) && Number(e[0]))
            .map((e: any) => [Number(e[0]), Number(e[1]) || 0] as [number, number])
    };
}

/** 유물 주스탯 합 — 체력·공격력·방어력 평옵 */
export function artifactMains(arts: Artifact[]): { hp: number; atk: number; def: number } {
    const out = { hp: 0, atk: 0, def: 0 };
    for (const a of arts) {
        if (a.main[0] === 100) out.hp += a.main[1];
        else if (a.main[0] === 101) out.atk += a.main[1];
        else if (a.main[0] === 102) out.def += a.main[1];
    }
    return out;
}

/** 아티팩트 공격력 — 늘 이 값으로 본다 (공격력 +100 짜리 둘) */
export const ARTIFACT_ATK_DEFAULT = 200;

// ── 유물(relic) ─────────────────────────────────────────────────────────────

/**
 * 몹에 새긴 유물 하나. 아티팩트(둥근 것)와 다른, 룬 창 오른쪽의 육각 유물이다.
 * 실측 생김새: `{ type, upgrade_curr, durability, pri_effect: [100 체력% | 101 공격력% | 102 방어력%, 값],
 * sec_effect: [종류(=type), 값, 등급] }`. 종류별 이름표는 아직 못 구해 번호로 적는다.
 */
export interface Relic {
    rid: number;
    type: number;
    level: number;
    durability: number;
    /** [100 체력% | 101 공격력% | 102 방어력%, 값(%)] */
    main: [number, number];
    /** [효과 종류, 값, 등급] */
    sub: [number, number, number];
}

export const RELIC_MAIN: Record<number, string> = { 100: '체력', 101: '공격력', 102: '방어력' };

export function relicOf(raw: any): Relic | null {
    if (!raw || !Number(raw.rid)) return null;
    const pri = Array.isArray(raw.pri_effect) ? raw.pri_effect : [];
    const sec = Array.isArray(raw.sec_effect) ? raw.sec_effect : [];
    return {
        rid: Number(raw.rid),
        type: Number(raw.type) || 0,
        level: Number(raw.upgrade_curr) || 0,
        durability: Number(raw.durability) || 0,
        main: [Number(pri[0]) || 0, Number(pri[1]) || 0],
        sub: [Number(sec[0]) || 0, Number(sec[1]) || 0, Number(sec[2]) || 0]
    };
}

/** 유물 주효과 합 — 체력·공격력·방어력 퍼센트 */
export function relicMains(relics: Relic[]): { hpPct: number; atkPct: number; defPct: number } {
    const out = { hpPct: 0, atkPct: 0, defPct: 0 };
    for (const r of relics) {
        if (r.main[0] === 100) out.hpPct += r.main[1];
        else if (r.main[0] === 101) out.atkPct += r.main[1];
        else if (r.main[0] === 102) out.defPct += r.main[1];
    }
    return out;
}

// ── 몹과 스탯 ────────────────────────────────────────────────────────────────

/** 계정의 몹 하나 — 룬을 끼워 볼 대상 */
export interface Unit {
    unitId: number;
    unitMasterId: number;
    level: number;
    stars: number;
    /** 1 물 · 2 불 · 3 바람 · 4 빛 · 5 어둠 */
    attribute: number;
    base: { hp: number; atk: number; def: number; spd: number; critRate: number; critDmg: number; resist: number; accuracy: number };
    /** 낀 아티팩트 (없으면 빈 배열) */
    artifacts: Artifact[];
    /** 새긴 유물 (없으면 빈 배열) */
    relics: Relic[];
}

export const ATTRIBUTE_NAME: Record<number, string> = { 1: '물', 2: '불', 3: '바람', 4: '빛', 5: '어둠' };
export const ATTRIBUTE_KEY: Record<number, string> = { 1: 'Water', 2: 'Fire', 3: 'Wind', 4: 'Light', 5: 'Dark' };

/** 계정 JSON 의 몹 전부(창고 포함). 체력은 con × 15 */
export function unitsFromAccount(account: any): Unit[] {
    const out: Unit[] = [];
    const all = [...(account?.unit_list || []), ...(account?.unit_storage_normal_list || [])];
    for (const u of all) {
        const id = Number(u?.unit_id);
        const master = Number(u?.unit_master_id);
        if (!id || !master) continue;
        out.push({
            unitId: id,
            unitMasterId: master,
            level: Number(u.unit_level) || 0,
            stars: Number(u.class) || 0,
            attribute: Number(u.attribute) || 0,
            base: {
                hp: (Number(u.con) || 0) * 15,
                atk: Number(u.atk) || 0,
                def: Number(u.def) || 0,
                spd: Number(u.spd) || 0,
                critRate: Number(u.critical_rate) || 15,
                critDmg: Number(u.critical_damage) || 50,
                resist: Number(u.resist) || 0,
                accuracy: Number(u.accuracy) || 0
            },
            artifacts: (Array.isArray(u.artifacts) ? u.artifacts : []).map(artifactOf).filter((a: Artifact | null): a is Artifact => !!a),
            relics: (Array.isArray(u.relics) ? u.relics : []).map(relicOf).filter((r: Relic | null): r is Relic => !!r)
        });
    }
    return out;
}

export interface StatLine { key: string; label: string; base: number; bonus: number; pct: boolean }

/**
 * **몹에 룬 여섯 개를 끼웠을 때의 스탯** — 게임 몹 창처럼 "기본 + 룬으로 오른 만큼".
 *
 *   오른 만큼 = ⌈평옵 합 + 기본 × 퍼센트 합 / 100⌉ + 세트 효과
 * 퍼센트는 기본값에만 붙고, **반올림이 아니라 올림**이다 — 루셴 방어 461 × 15% = 69.15 를
 * 게임은 70 으로 올려 160 + 70 = 230 을 보여 준다(반올림하면 229 로 1 이 모자랐다). 세트 효과: 활력 체력 15% · 수호 방어 15% ·
 * 맹공 공격 35% · 신속 공속 25% · 칼날 치확 12 · 격노 치피 40 · 집중 효적 20 · 인내 효저 20.
 * **아티팩트는 늘 공격력 +200 으로 본다** — 몹이 실제로 뭘 꼈든(계정에 같은 몹이 여럿이면 어느
 * 놈을 집었느냐로 숫자가 흔들렸다) 점령전 조커는 공격력 +100 둘을 낀다는 전제다(사용자 지시).
 * 유물은 그 몹에 새긴 것의 주효과(체력·공격력·방어력 %)를 넣는다. 부효과는 조건부라 안 넣는다.
 */
export function statsWith(unit: Unit, runes: Rune[], artifacts?: Artifact[], relics: Relic[] = unit.relics): StatLine[] {
    /**
     * 아티팩트 주옵(체력·공격력·방어력 평옵).
     *
     * 옵티마이저는 몹마다 아티팩트가 달라 **늘 공격력 +200** 으로 본다(`ARTIFACT_ATK_DEFAULT`).
     * 전투기록처럼 **실제 아티팩트를 아는 곳**은 넘겨주면 그 값을 쓴다 — 넘기지 않으면 예전 그대로다.
     */
    const art = artifacts ? artifactMains(artifacts) : { hp: 0, atk: ARTIFACT_ATK_DEFAULT, def: 0 };
    const rel = relicMains(relics);
    const flat: Record<number, number> = {};
    const pct: Record<number, number> = {};
    for (const r of runes) {
        for (const [t, v] of Object.entries(r.total)) {
            const type = Number(t);
            if (type === EFF.HP_PCT || type === EFF.ATK_PCT || type === EFF.DEF_PCT) pct[type] = (pct[type] || 0) + v;
            else flat[type] = (flat[type] || 0) + v;
        }
    }
    const count = setCounts(runes);
    const full = (id: number) => Math.floor((count[id] || 0) / piecesOf(id));
    const b = unit.base;
    const hpPct = (pct[EFF.HP_PCT] || 0) + 15 * full(1) + rel.hpPct;
    const atkPct = (pct[EFF.ATK_PCT] || 0) + 35 * full(8) + rel.atkPct;
    const defPct = (pct[EFF.DEF_PCT] || 0) + 15 * full(2) + rel.defPct;
    const spdPct = 25 * full(3);
    return [
        { key: 'hp', label: '체력', base: b.hp, bonus: Math.ceil((flat[EFF.HP_FLAT] || 0) + b.hp * hpPct / 100) + art.hp, pct: false },
        { key: 'atk', label: '공격력', base: b.atk, bonus: Math.ceil((flat[EFF.ATK_FLAT] || 0) + b.atk * atkPct / 100) + art.atk, pct: false },
        { key: 'def', label: '방어력', base: b.def, bonus: Math.ceil((flat[EFF.DEF_FLAT] || 0) + b.def * defPct / 100) + art.def, pct: false },
        { key: 'spd', label: '공격속도', base: b.spd, bonus: Math.ceil((flat[EFF.SPD] || 0) + b.spd * spdPct / 100), pct: false },
        { key: 'cr', label: '치명 확률', base: b.critRate, bonus: (flat[EFF.CRIT_RATE] || 0) + 12 * full(4), pct: true },
        { key: 'cd', label: '치명 피해', base: b.critDmg, bonus: (flat[EFF.CRIT_DMG] || 0) + 40 * full(5), pct: true },
        { key: 'res', label: '효과 저항', base: b.resist, bonus: (flat[EFF.RESIST] || 0) + 20 * full(7), pct: true },
        { key: 'acc', label: '효과 적중', base: b.accuracy, bonus: (flat[EFF.ACCURACY] || 0) + 20 * full(6), pct: true }
    ];
}

// ── 세트별 최고속도 ─────────────────────────────────────────────────────────

export interface SpeedCombo { key: string; label: string; four: number; pair: number | null }

/** 점령전에서 쓰는 여섯 조합 — 4벌 세트 + (의지 2벌 | 아무거나) */
export const SPEED_COMBOS: SpeedCombo[] = [
    { key: 'swift-any', label: '신잡 (신속 + 잡)', four: 3, pair: null },
    { key: 'swift-will', label: '신의 (신속 + 의지)', four: 3, pair: 15 },
    { key: 'violent-any', label: '폭잡 (폭주 + 잡)', four: 13, pair: null },
    { key: 'violent-will', label: '폭의 (폭주 + 의지)', four: 13, pair: 15 },
    { key: 'despair-any', label: '절잡 (절망 + 잡)', four: 10, pair: null },
    { key: 'despair-will', label: '절의 (절망 + 의지)', four: 10, pair: 15 }
];

export interface SpeedBuild {
    /** 룬 합계 + 세트 보너스 — **기본속도는 뺀 값**이다. 사람들이 "234속 신잡" 이라 부르는 그 수 */
    total: number;
    /** 기본속도까지 더한 실제 공격속도 */
    final: number;
    runeSum: number;
    setBonus: number;
    runes: Rune[];   // 1~6번 순
}

/** 여섯 자리 중 네 자리를 고르는 15가지 */
const FOUR_OF_SIX: number[][] = (() => {
    const out: number[][] = [];
    for (let a = 1; a <= 6; a++) for (let b = a + 1; b <= 6; b++) for (let c = b + 1; c <= 6; c++) for (let d = c + 1; d <= 6; d++) out.push([a, b, c, d]);
    return out;
})();

const spdOf = (r: Rune) => r.total[EFF.SPD] || 0;

/**
 * **4번·6번 주옵 고르기** (사용자 2026-10-21 — "속체체" 처럼). 자리 번호 → 주옵 효과 번호, 없거나 null 이면 아무거나.
 * 2번은 늘 공속 주옵이다(아래 bestSpeedBuild).
 */
export type MainPick = { 4?: number | null; 6?: number | null };
/** 4번에 붙는 주옵 — 체%·공%·방%·치확·치피 */
export const SLOT4_MAINS: number[] = [EFF.HP_PCT, EFF.ATK_PCT, EFF.DEF_PCT, EFF.CRIT_RATE, EFF.CRIT_DMG];
/** 6번에 붙는 주옵 — 체%·공%·방%·효저·효적 */
export const SLOT6_MAINS: number[] = [EFF.HP_PCT, EFF.ATK_PCT, EFF.DEF_PCT, EFF.RESIST, EFF.ACCURACY];
const mainOk = (r: Rune, mains: MainPick) => {
    if (r.slot === 2) return r.main[0] === EFF.SPD;
    const want = r.slot === 4 ? mains[4] : r.slot === 6 ? mains[6] : null;
    return want == null || r.main[0] === want;
};

/**
 * 한 조합의 최고속도 한 벌.
 *
 * 자리마다 가장 빠른 룬을 고르면 되는데, 어느 네 자리가 4벌 세트를 맡느냐에 따라 답이 달라서
 * 열다섯 가지 배치를 다 본다. **2번 룬은 속도 주옵션이어야** 한다(아니면 최고속도가 아니다).
 * 신속 4벌은 기본속도의 25% 를 더한다.
 *
 * `total` 은 기본속도를 뺀 "룬 + 세트" 값이다 — 룬 자체를 견주는 수라서 그렇고, 210·220
 * 같은 기준선도 이 값으로 판정한다(swsiege 와 같은 관례).
 */
export function bestSpeedBuild(runes: Rune[], combo: SpeedCombo, base: number, used: Set<number>, mains: MainPick = {}): SpeedBuild | null {
    const bySlot: Rune[][] = [[], [], [], [], [], [], []];
    for (const r of runes) {
        if (used.has(r.id) || r.slot < 1 || r.slot > 6) continue;
        if (!mainOk(r, mains)) continue;
        bySlot[r.slot].push(r);
    }
    for (let s = 1; s <= 6; s++) bySlot[s].sort((a, b) => spdOf(b) - spdOf(a));

    const pickBest = (slot: number, want: number | null) => {
        for (const r of bySlot[slot]) if (want === null || r.set === want) return r;
        return null;
    };

    let best: SpeedBuild | null = null;
    for (const fourSlots of FOUR_OF_SIX) {
        const chosen: Rune[] = [];
        let ok = true;
        for (let s = 1; s <= 6; s++) {
            const r = pickBest(s, fourSlots.includes(s) ? combo.four : combo.pair);
            if (!r) { ok = false; break; }
            chosen.push(r);
        }
        if (!ok) continue;
        const runeSum = chosen.reduce((a, r) => a + spdOf(r), 0);
        const setBonus = combo.four === 3 ? Math.round(base * 0.25) : 0;
        const total = runeSum + setBonus;
        if (!best || total > best.total) best = { total, final: base + total, runeSum, setBonus, runes: chosen };
    }
    return best;
}

/**
 * 정해진 룬 여섯 개의 "룬 + 세트" 속도. 벌을 고르는 `bestSpeedBuild` 와 달리 **주어진 여섯
 * 개를 그대로** 셈한다 — 화면에서 "끝까지 키우면 얼마나 더 빨라지나(+N)" 를 낼 때 쓴다.
 * 신속 4벌이면 기본속도의 25% 가 붙으므로 고른 몹의 기본속도를 같이 받는다.
 */
export function speedTotalOf(runes: Rune[], combo: SpeedCombo, base: number): number {
    const runeSum = runes.reduce((a, r) => a + spdOf(r), 0);
    const setBonus = combo.four === 3 ? Math.round(base * 0.25) : 0;
    return runeSum + setBonus;
}

/** 1~n 순위 — 앞 순위에 쓴 룬은 뺀다 */
export function rankSpeed(runes: Rune[], combo: SpeedCombo, base: number, n = 3, mains: MainPick = {}): SpeedBuild[] {
    const used = new Set<number>();
    const out: SpeedBuild[] = [];
    for (let i = 0; i < n; i++) {
        const b = bestSpeedBuild(runes, combo, base, used, mains);
        if (!b) break;
        out.push(b);
        for (const r of b.runes) used.add(r.id);
    }
    return out;
}

/**
 * 기준 속도 이상으로 **겹치지 않게** 몇 벌이 나오나.
 *
 * 가장 빠른 벌부터 빼 가면 빠른 룬을 한 벌이 다 끌어안아 벌 수가 준다. 그래서 한 벌을 찾은
 * 뒤 **기준선을 넘는 한도 안에서 자리마다 가장 느린 룬으로 바꿔 끼운다**(남는 속도만큼만).
 * 빠른 룬이 다음 벌에 남아 벌 수가 는다 — swsiege 가 210 이상 7벌을 세는 것과 같은 결이다.
 */
export function setsAbove(runes: Rune[], combo: SpeedCombo, base: number, threshold: number, cap = 12, mains: MainPick = {}): SpeedBuild[] {
    const used = new Set<number>();
    const out: SpeedBuild[] = [];
    while (out.length < cap) {
        const best = bestSpeedBuild(runes, combo, base, used, mains);
        if (!best || best.total < threshold) break;

        // 이 벌에서 4벌 세트를 맡은 자리는 그대로 두고, 자리마다 더 느린 룬으로 내린다
        const fourSlots = new Set(best.runes.filter((r) => r.set === combo.four).map((r) => r.slot));
        const chosen = best.runes.slice();
        let slack = best.total - threshold;
        // 빠른 룬을 맡은 자리부터 내려야 빠른 룬이 많이 풀린다
        const order = chosen.map((r, i) => ({ i, spd: spdOf(r) })).sort((a, b) => b.spd - a.spd).map((x) => x.i);
        for (const i of order) {
            const cur = chosen[i];
            const want = fourSlots.has(cur.slot) ? combo.four : combo.pair;
            let swap: Rune | null = null;
            for (const r of runes) {
                if (r.id === cur.id || used.has(r.id) || r.slot !== cur.slot) continue;
                if (want !== null && r.set !== want) continue;
                if (!mainOk(r, mains)) continue;
                if (chosen.some((c) => c.id === r.id)) continue;
                const drop = spdOf(cur) - spdOf(r);
                if (drop <= 0 || drop > slack) continue;
                if (!swap || spdOf(r) < spdOf(swap)) swap = r;   // 한도 안에서 가장 느린 것
            }
            if (swap) { slack -= spdOf(cur) - spdOf(swap); chosen[i] = swap; }
        }
        const runeSum = chosen.reduce((a, r) => a + spdOf(r), 0);
        const total = runeSum + best.setBonus;
        out.push({ total, final: base + total, runeSum, setBonus: best.setBonus, runes: chosen });
        for (const r of chosen) used.add(r.id);
    }
    return out;
}

/** 기준선 — 신속 조합은 210·220·230, 나머지는 180·190·200 */
export function thresholdsOf(combo: SpeedCombo): number[] {
    return combo.four === 3 ? [210, 220, 230] : [180, 190, 200];
}

// ── 최고 전투력 (조커 기준) ────────────────────────────────────────────────────

/**
 * 조커(루셴) 6성 40레벨 기본값.
 *
 * 전투력은 사용자가 쓰는 셈 그대로 — **(뒷공 + 200) + 치피 × 10**:
 *   뒷공 = 룬으로 오른 공격력(평공 + 900 × 공%/100, 건물은 넣지 않는다)
 *   + 아티팩트 공격력 = 그 몹이 낀 아티팩트 둘의 공격력 주스탯 합 (둘 다 +100 이면 200)
 *   + 유물 공격력% = 그 몹에 새긴 유물의 공격력 주효과 (900 × %/100)
 *   치피 = 기본 50 + 룬 치피 + 격노 4벌 40
 * "지금 룬 그대로" 의 값이라 강화·건물 같은 건 아무것도 안 얹는다. swsiege 의 "조커 전투력"
 * 은 여기에 공격력 건물과 치확·상수를 더 얹은 수라(애플톡 5144 = 4845 + 299) 같지 않다 —
 * 예전엔 그 수에 맞췄다가 사용자 지시로 이 식으로 돌아왔다.
 */
export const JOKER = { atk: 900, critRate: 15, critDmg: 50 };

/**
 * 전투력을 셈할 때 쓰는 몹의 밑값. 고른 몹의 `base` 에서 가져온다 — 예전에는 조커 값이 박혀
 * 있어 **몹을 바꿔도 전투력이 안 바뀌었다**(몬스터 스탯 창과도 어긋났다). 몹을 못 고른
 * 때에만 `JOKER` 로 대신한다.
 */
export interface PowerBase { atk: number; critRate: number; critDmg: number }

export interface PowerCombo { key: string; label: string; four: number; pair: number | null }

/**
 * 격노 + 칼날 하나뿐이다.
 *
 * 식이 세트 효과를 안 세므로(swsiege 와 같게 맞춘 결과) 맹공을 같은 식에 넣으면 맹공 4벌의
 * 공% 만 들어가 늘 격노를 이긴다 — 실제 게임과 반대다. swsiege 도 격칼로 고정이라 견줄
 * 상대도 없어서 맹공 카드는 뺐다. "잡" 도 두지 않는다 — 남는 두 자리는 늘 칼날을 고른다.
 */
export const POWER_COMBOS: PowerCombo[] = [
    { key: 'rage-blade', label: '격노 + 칼날', four: 5, pair: 4 }
];

export interface PowerBuild {
    power: number;
    /** 전투력에 얹은 아티팩트 공격력 */
    artifactAtk: number;
    /** 전투력에 얹은 유물 공격력 % */
    relicAtkPct: number;
    atk: number;
    critRate: number;
    critDmg: number;
    sets: string[];
    runes: Rune[];
}

/** 세트 효과 — 스탯이 오르는 것만 */
function setBonusOf(runes: Rune[]): { atkPct: number; critRate: number; critDmg: number; sets: string[] } {
    const count = setCounts(runes);
    let atkPct = 0, critRate = 0, critDmg = 0;
    const sets: string[] = [];
    for (const [idStr, n] of Object.entries(count)) {
        const id = Number(idStr);
        const full = Math.floor(n / piecesOf(id));
        if (full < 1) continue;
        for (let i = 0; i < full; i++) sets.push(setName(id));
        if (id === 8) atkPct += 35 * full;
        if (id === 4) critRate += 12 * full;
        if (id === 5) critDmg += 40 * full;
    }
    return { atkPct, critRate, critDmg, sets };
}

/**
 * 정해진 룬 여섯 개의 전투력. 조합을 고르는 `bestPowerBuild` 와 달리 **주어진 여섯 개를 그대로**
 * 셈한다 — 화면에서 "끝까지 키우면 얼마나 더 오르나(+N)" 를 낼 때 같은 여섯 개의 끝값을 넣는다.
 */
export function powerOf(runes: Rune[], bonus?: AccountBonus, artifactAtk = ARTIFACT_ATK_DEFAULT, relicAtkPct = 0, mob: PowerBase = JOKER): PowerBuild {
    let atkPct = 0, atkFlat = 0, cr = 0, cd = 0;
    for (const r of runes) {
        atkPct += r.total[EFF.ATK_PCT] || 0;
        atkFlat += r.total[EFF.ATK_FLAT] || 0;
        cr += r.total[EFF.CRIT_RATE] || 0;
        cd += r.total[EFF.CRIT_DMG] || 0;
    }
    const setB = setBonusOf(runes);
    void bonus;   // 건물은 전투력에 안 넣는다(위 주석) — 인자는 화면 호환용으로 남긴다
    const atkBonus = Math.ceil(mob.atk * (atkPct + setB.atkPct + relicAtkPct) / 100 + atkFlat) + artifactAtk;   // 뒷공 + 아티팩트 + 유물 (게임처럼 올림)
    const atk = mob.atk + atkBonus;
    const critRate = Math.min(100, mob.critRate + cr + setB.critRate);
    const critDmg = mob.critDmg + cd + setB.critDmg;
    const power = atkBonus + critDmg * 10;
    return { power, artifactAtk, relicAtkPct, atk, critRate, critDmg, sets: setB.sets, runes };
}

/**
 * 한 조합의 최고 전투력.
 *
 * 룬이 수천 개라 다 볼 수는 없다. 자리마다 "공%·치피·치확·평공" 을 대충 더한 값으로 위에서
 * 여섯 개만 남기고, 열다섯 가지 자리 배치 × 6^6 을 다 본다(수십만 번, 브라우저에서 1초 안).
 * 치확은 100 에서 잘리므로 셈은 정확한 식으로 한다(대충 더한 값은 후보를 고를 때만 쓴다).
 */
export function bestPowerBuild(runes: Rune[], combo: PowerCombo, bonus?: AccountBonus, artifactAtk = ARTIFACT_ATK_DEFAULT, relicAtkPct = 0, mob: PowerBase = JOKER, topK = 6): PowerBuild | null {
    const proxy = (r: Rune) =>
        (r.total[EFF.ATK_PCT] || 0) + (r.total[EFF.CRIT_DMG] || 0) + (r.total[EFF.CRIT_RATE] || 0) * 1.2 + (r.total[EFF.ATK_FLAT] || 0) / 9;

    /**
     * **치확 100 을 먼저 채운다.** 식에서 치확은 1점에 3 이라 그냥 두면 치확 71 짜리가 1등이 되는데,
     * 조커는 치명타가 안 뜨면 아무 의미가 없다(swsiege 도 치확 100 인 조합을 고른다 — 실측).
     * 100 을 채우는 조합이 하나도 없을 때만 그냥 가장 높은 것을 준다.
     */
    const better = (a: PowerBuild, b: PowerBuild | null) => {
        if (!b) return true;
        const aFull = a.critRate >= 100, bFull = b.critRate >= 100;
        if (aFull !== bFull) return aFull;
        return a.power > b.power;
    };

    const bySlotSet = (slot: number, want: number | null) =>
        runes.filter((r) => r.slot === slot && (want === null || r.set === want))
            .sort((a, b) => proxy(b) - proxy(a)).slice(0, topK);

    let best: PowerBuild | null = null;
    for (const fourSlots of FOUR_OF_SIX) {
        const cands: Rune[][] = [];
        let ok = true;
        for (let s = 1; s <= 6; s++) {
            const c = bySlotSet(s, fourSlots.includes(s) ? combo.four : combo.pair);
            if (!c.length) { ok = false; break; }
            cands.push(c);
        }
        if (!ok) continue;

        const pick: Rune[] = new Array(6);
        const walk = (i: number) => {
            if (i === 6) {
                const b = powerOf(pick.slice(), bonus, artifactAtk, relicAtkPct, mob);
                if (better(b, best)) best = b;
                return;
            }
            for (const r of cands[i]) { pick[i] = r; walk(i + 1); }
        };
        walk(0);
    }
    return best;
}

// ── 드데조 (풍조커 · 풍드나 · 빛데몬) ──────────────────────────────────────────

/** 룬 여섯 개가 더하는 공격속도(평속) — 격노·칼날에는 신속이 없어 세트는 안 센다 */
export const runeSpdOf = (runes: Rune[]) => runes.reduce((a, r) => a + (r.total[EFF.SPD] || 0), 0);

/**
 * 격노 4 + 칼날 2 로 고정한 여섯 자리를 **더하기만으로** 훑는다 — 드데조는 몹 셋 × 수백만 갈래라 갈래마다 `powerOf` 를
 * 부르면 브라우저에서 10초가 넘었다(실측 11초). 자리마다 세트를 정해 두고 고르므로 세트 효과는 어느 갈래나 같아서
 * 한 번만 센다. `visit` 에는 그 갈래의 전투력·치확·룬 공속 합과 고른 룬(재사용하는 배열 — 남기려면 복사)을 준다.
 */
function walkPower(
    runes: Rune[], combo: PowerCombo, mob: PowerBase, relicAtkPct: number, artifactAtk: number,
    topPower: number, topSpeed: number,
    visit: (power: number, critRate: number, spd: number, pick: Rune[]) => void
) {
    const proxy = (r: Rune) =>
        (r.total[EFF.ATK_PCT] || 0) + (r.total[EFF.CRIT_DMG] || 0) + (r.total[EFF.CRIT_RATE] || 0) * 1.2 + (r.total[EFF.ATK_FLAT] || 0) / 9;
    const fast = (r: Rune) => proxy(r) + (r.total[EFF.SPD] || 0) * 2;
    const bySlotSet = (slot: number, want: number | null) => {
        const all = runes.filter((r) => r.slot === slot && (want === null || r.set === want));
        const a = [...all].sort((x, y) => proxy(y) - proxy(x)).slice(0, topPower);
        const b = topSpeed > 0 ? [...all].sort((x, y) => fast(y) - fast(x)).filter((r) => !a.includes(r)).slice(0, topSpeed) : [];
        return [...a, ...b];
    };
    for (const fourSlots of FOUR_OF_SIX) {
        const cands: Rune[][] = [];
        let ok = true;
        for (let s = 1; s <= 6; s++) {
            const c = bySlotSet(s, fourSlots.includes(s) ? combo.four : combo.pair);
            if (!c.length) { ok = false; break; }
            cands.push(c);
        }
        if (!ok) continue;
        const setB = setBonusOf(cands.map((c) => c[0]));
        const ap = cands.map((c) => c.map((r) => r.total[EFF.ATK_PCT] || 0));
        const af = cands.map((c) => c.map((r) => r.total[EFF.ATK_FLAT] || 0));
        const crv = cands.map((c) => c.map((r) => r.total[EFF.CRIT_RATE] || 0));
        const cdv = cands.map((c) => c.map((r) => r.total[EFF.CRIT_DMG] || 0));
        const sp = cands.map((c) => c.map((r) => r.total[EFF.SPD] || 0));
        const pick: Rune[] = new Array(6);
        const walk = (i: number, aP: number, aF: number, cR: number, cD: number, sP: number) => {
            if (i === 6) {
                const atkBonus = Math.ceil(mob.atk * (aP + setB.atkPct + relicAtkPct) / 100 + aF) + artifactAtk;
                const critRate = Math.min(100, mob.critRate + cR + setB.critRate);
                const power = atkBonus + (mob.critDmg + cD + setB.critDmg) * 10;
                visit(power, critRate, sP, pick);
                return;
            }
            const c = cands[i];
            for (let k = 0; k < c.length; k++) {
                pick[i] = c[k];
                walk(i + 1, aP + ap[i][k], aF + af[i][k], cR + crv[i][k], cD + cdv[i][k], sP + sp[i][k]);
            }
        };
        walk(0, 0, 0, 0, 0, 0);
    }
}

/**
 * 한 몹의 **속도별 최고 전투력** — 룬 공속 합마다 센 세팅 셋씩. 드데조가 "드나는 느리게 / 조커·데몬은 빠르게" 를
 * 견줄 때 쓴다. 자리마다 전투력 쪽 후보 `topPower` 개에 속도 쪽 후보 `topSpeed` 개를 더해 본다(속도가 다른 세팅이
 * 나오게). 치확 100 을 채운 세팅이 하나라도 있으면 그것들만 남긴다(bestPowerBuild 와 같은 규칙).
 */
export function powerFront(runes: Rune[], combo: PowerCombo, bonus?: AccountBonus, artifactAtk = ARTIFACT_ATK_DEFAULT, relicAtkPct = 0, mob: PowerBase = JOKER, topPower = 5, topSpeed = 2): PowerBuild[] {
    // 속도마다 센 것 셋 — 하나만 두면 다른 몹과 룬이 겹칠 때 갈아 끼울 것이 없다
    const KEEP = 3;
    const full = new Map<number, Array<{ power: number; runes: Rune[] }>>();
    const part = new Map<number, Array<{ power: number; runes: Rune[] }>>();
    walkPower(runes, combo, mob, relicAtkPct, artifactAtk, topPower, topSpeed, (power, critRate, spd, pick) => {
        const into = critRate >= 100 ? full : part;
        const cur = into.get(spd) || [];
        if (cur.length >= KEEP && power <= cur[cur.length - 1].power) return;
        cur.push({ power, runes: pick.slice() });
        cur.sort((x, y) => y.power - x.power);
        if (cur.length > KEEP) cur.pop();
        into.set(spd, cur);
    });
    return Array.from((full.size ? full : part).values()).flat()
        .map((x) => powerOf(x.runes, bonus, artifactAtk, relicAtkPct, mob))
        .sort((a, b) => b.power - a.power);
}

/** bestPowerBuild 와 같은 답(치확 100 먼저, 그다음 전투력)을 더하기만으로 — 드데조의 "순서대로 빼기" 에 쓴다 */
function fastBestPower(runes: Rune[], combo: PowerCombo, bonus: AccountBonus | undefined, artifactAtk: number, relicAtkPct: number, mob: PowerBase): PowerBuild | null {
    let best: { full: boolean; power: number; runes: Rune[] } | null = null;
    walkPower(runes, combo, mob, relicAtkPct, artifactAtk, 6, 0, (power, critRate, _spd, pick) => {
        const full = critRate >= 100;
        if (best && (best.full && !full || (best.full === full && power <= best.power))) return;
        best = { full, power, runes: pick.slice() };
    });
    const b = best as { full: boolean; power: number; runes: Rune[] } | null;
    return b ? powerOf(b.runes, bonus, artifactAtk, relicAtkPct, mob) : null;
}

export interface DdjMob { base: PowerBase; spd: number; relicAtkPct: number }
export interface DdjResult { builds: Array<PowerBuild | null>; speeds: Array<number | null> }

/**
 * **드데조 셋을 한꺼번에** — [0]=풍조커, [1]=풍드나, [2]=빛데몬 (사용자 2026-10-21).
 *
 * 조건: 룬은 셋이 겹치지 않는다, **풍조커·빛데몬은 풍드나보다 느리지 않다**(같으면 된다). 드나를 느리게 맞추든 조커·데몬을
 * 빠르게 맞추든 **셋의 전투력 합이 가장 높은 쪽**을 고른다(합이 같으면 조커, 그다음 드나 전투력이 높은 쪽).
 * 몹마다 속도별 최고 세팅(powerFront)을 뽑아 세 개 짝을 다 본다 — 위에서부터 보고 남은 몫으로 못 넘는 갈래는 잘라 낸다.
 * 짝이 하나도 없으면(남는 룬이 모자람) 예전처럼 순서대로 빼며 맞춘다.
 */
export function ddjBuilds(runes: Rune[], mobs: DdjMob[], bonus?: AccountBonus, artifactAtk = ARTIFACT_ATK_DEFAULT): DdjResult {
    const combo = POWER_COMBOS[0];
    const fronts = mobs.map((m) => powerFront(runes, combo, bonus, artifactAtk, m.relicAtkPct, m.base));
    const speedOf = (k: number, b: PowerBuild) => mobs[k].spd + runeSpdOf(b.runes);
    const [J, D, M] = fronts;
    let best: { sum: number; j: PowerBuild; d: PowerBuild; m: PowerBuild } | null = null;
    const topM = M.length ? M[0].power : 0;
    const topD = D.length ? D[0].power : 0;
    const beats = (sum: number, j: PowerBuild, d: PowerBuild) =>
        !best || sum > best.sum || (sum === best.sum && (j.power > best.j.power || (j.power === best.j.power && d.power > best.d.power)));
    for (const j of J) {
        if (best && j.power + topD + topM < best.sum) break;
        const jIds = new Set(j.runes.map((r) => r.id));
        const js = speedOf(0, j);
        for (const d of D) {
            if (best && j.power + d.power + topM < best.sum) break;
            const ds = speedOf(1, d);
            if (ds > js) continue;
            if (d.runes.some((r) => jIds.has(r.id))) continue;
            for (const m of M) {
                const sum = j.power + d.power + m.power;
                if (best && sum < best.sum) break;
                if (speedOf(2, m) < ds) continue;
                if (m.runes.some((r) => jIds.has(r.id) || d.runes.some((x) => x.id === r.id))) continue;
                if (beats(sum, j, d)) best = { sum, j, d, m };
                break;   // m 은 전투력 순이라 이 j·d 에서는 처음 맞는 것이 가장 세다
            }
        }
    }
    // 순서대로 빼며 맞춘 것도 조건을 지키면 후보다 — 몹마다 따로 뽑은 후보끼리는 룬이 겹쳐 놓치는 짝이 있다(실측)
    let pool0 = runes;
    const greedy = mobs.map((m) => {
        const b = fastBestPower(pool0, combo, bonus, artifactAtk, m.relicAtkPct, m.base);
        if (b) { const used = new Set(b.runes.map((r) => r.id)); pool0 = pool0.filter((r) => !used.has(r.id)); }
        return b;
    });
    if (greedy.every(Boolean)) {
        const [gj, gd, gm] = greedy as PowerBuild[];
        const ok = speedOf(0, gj) >= speedOf(1, gd) && speedOf(2, gm) >= speedOf(1, gd);
        const sum = gj.power + gd.power + gm.power;
        if (ok && beats(sum, gj, gd)) best = { sum, j: gj, d: gd, m: gm };
    }
    if (best) {
        const b = best as { j: PowerBuild; d: PowerBuild; m: PowerBuild };
        const list = [b.j, b.d, b.m];
        return { builds: list, speeds: list.map((x, k) => speedOf(k, x)) };
    }
    // 짝이 없다 — 순서대로 빼며 맞춘 것을 그대로 준다(속도 조건은 못 지킨다)
    return { builds: greedy, speeds: greedy.map((x, k) => (x ? speedOf(k, x) : null)) };
}
